import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import api from '@/utils/api';
import { buildSignBroadcast } from '@/utils/kleverTx';
import { useAppSelector } from '@/redux/Store';
import { formatPrice } from '@/utils/formatPrice';
import './PaywallModal.scss';

interface PPVQuote {
  pricing: {
    ppvPrice: string;
    estimatedGas: string;
    totalTransaction: string;
  };
  balance: {
    current: number;
    required: string;
    shortfall: string | null;
  };
  canProceed: boolean;
  warning?: string;
}

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUnlocked: () => void;
  /** 'paid' = view fee (permanent) OR subscription; 'ppv' = per-post permanent */
  viewType: 'paid' | 'ppv';
  postId: string;
  profileId: number;
  ppvPrice?: string | null;
  ppvTokenId?: string | null;
  /** View fee amount in display units (e.g. "5.00") */
  viewFee?: string | null;
  /** Subscription fee in display units */
  subscriptionFee?: string | null;
  /** Subscription period in seconds */
  subscriptionPeriodSeconds?: number | null;
  creatorName?: string;
}

type Step = 'idle' | 'signing' | 'confirming' | 'unlocking' | 'done' | 'error';

/** Convert display KLYX amount (e.g. "1.00") to smallest-unit integer string ("100000000"). */
function toSmallestUnit(display: string): string {
  const [whole = '0', frac = ''] = display.split('.');
  const fracPadded = frac.padEnd(8, '0').slice(0, 8);
  return (BigInt(whole) * 100_000_000n + BigInt(fracPadded)).toString();
}

const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  onUnlocked,
  viewType,
  postId,
  profileId,
  ppvPrice,
  ppvTokenId,
  viewFee,
  subscriptionFee,
  subscriptionPeriodSeconds,
  creatorName,
}) => {
  const authUser = useAppSelector((state) => state.auth.user);
  const isEmailUser = authUser?.authMethod === 'email';

  const [step, setStep] = useState<Step>('idle');
  const [error, setError] = useState<string | null>(null);
  // Quote includes the real gas estimate (bandwidth + KApp fees) so
  // the button label matches the actual debit. Without this, the user
  // sees "Pay 1 KLV" but their wallet drops by ~8.9 KLV (#211 follow-up
  // for PPV). null = quote in flight; undefined = not requested yet.
  const [quote, setQuote] = useState<PPVQuote | null | undefined>(undefined);
  const [quoteLoading, setQuoteLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || viewType !== 'ppv' || !ppvPrice || !profileId) return;
    let cancelled = false;
    setQuoteLoading(true);
    setQuote(null);
    api.post('/posts/tx/quote-ppv', { profileId, postId, price: ppvPrice })
      .then((res) => {
        if (cancelled) return;
        setQuote(res.data as PPVQuote);
      })
      .catch(() => {
        if (cancelled) return;
        // Quote failure is non-fatal — the pay endpoint still does its
        // own preflight check. Just fall back to showing the bare
        // ppvPrice.
        setQuote(undefined);
      })
      .finally(() => {
        if (cancelled) return;
        setQuoteLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, viewType, postId, profileId, ppvPrice]);

  if (!isOpen) return null;

  const handlePayPPV = async () => {
    setError(null);
    setStep('signing');
    try {
      let txHash: string;
      if (isEmailUser) {
        // Email users: backend signs and broadcasts with custodial wallet
        const { data } = await api.post('/posts/tx/pay-ppv-email', {
          profileId,
          postId,
          price: ppvPrice,
        });
        txHash = data.txHash;
      } else {
        // Wallet users: sign with Klever Web Extension
        const { data } = await api.post('/posts/tx/pay-ppv', {
          profileId,
          postId,
          price: ppvPrice,
        });
        txHash = await buildSignBroadcast(data);
      }

      // Transaction confirmed — show loading state while PostCard fetches content
      setStep('unlocking');
      onUnlocked(); // PostCard handles closing modal + toast once content is ready

      api.post(`/posts/${postId}/confirm-ppv`, { txHash }).catch((err) => {
        console.warn('confirm-ppv cache write failed (access still valid on-chain):', err?.message);
      });
    } catch (err: any) {
      // Server-side preflight returns 400 + category:'insufficient_funds'
      // with a structured pricing + balance payload (#211 follow-up
      // for PPV). Surface that specifically so the user sees the
      // shortfall instead of a generic "transaction failed".
      const data = err?.response?.data;
      if (data?.category === 'insufficient_funds') {
        const msg = data.error || 'Insufficient funds';
        if (data.pricing && data.balance) {
          setQuote({
            pricing: data.pricing,
            balance: data.balance,
            canProceed: false,
            warning: msg,
          });
        }
        setError(msg);
        toast.error(msg);
      } else {
        const msg = data?.error || err?.message || 'Transaction failed';
        setError(msg);
        toast.error(msg);
      }
      setStep('error');
    }
  };

  const handlePayViewFee = async () => {
    setError(null);
    setStep('signing');
    try {
      let txHash: string;
      if (isEmailUser) {
        const { data } = await api.post('/posts/tx/pay-view-fee-email', {
          profileId,
          fee: viewFee,
        });
        txHash = data.txHash;
      } else {
        const { data } = await api.post('/posts/tx/pay-view-fee', {
          profileId,
          fee: viewFee,
        });
        txHash = await buildSignBroadcast(data);
      }
      // Record in DB cache (fire-and-forget — access is valid on-chain regardless)
      api.post(`/blockchain-profiles/${profileId}/access/view`, {
        txHash,
        paymentAmount: toSmallestUnit(viewFee || '0'),
      }).catch((err) => {
        console.warn('view_fee confirm write failed (on-chain still valid):', err?.message);
      });
      setStep('unlocking');
      onUnlocked();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Transaction failed';
      setError(msg);
      toast.error(msg);
      setStep('error');
    }
  };

  const handleSubscribe = async () => {
    setError(null);
    setStep('signing');
    try {
      if (isEmailUser) {
        await api.post(`/blockchain-profiles/${profileId}/subscribe-email`, {
          fee: subscriptionFee,
        });
      } else {
        const { data } = await api.post(`/blockchain-profiles/${profileId}/subscribe-build-tx`, {
          fee: subscriptionFee,
        });
        const txHash = await buildSignBroadcast(data);
        // Confirm with backend to record in DB
        await api.post(`/blockchain-profiles/${profileId}/subscribe`, {
          txHash,
          paymentAmount: toSmallestUnit(subscriptionFee || '0'),
        });
      }
      setStep('unlocking');
      onUnlocked();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Transaction failed';
      setError(msg);
      toast.error(msg);
      setStep('error');
    }
  };

  const isBusy = step === 'signing' || step === 'confirming' || step === 'unlocking';

  const modal = (
    <div className="paywall-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="paywall-modal">
        <button className="paywall-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="paywall-icon">🔒</div>
        <h2 className="paywall-title">
          {viewType === 'ppv' ? 'Unlock This Post' : 'Access Required'}
        </h2>
        {creatorName && (
          <p className="paywall-creator">by {creatorName}</p>
        )}

        {step === 'done' ? (
          <div className="paywall-success">
            <span className="success-icon">✅</span>
            <p>Access unlocked!</p>
          </div>
        ) : step === 'unlocking' ? (
          <div className="paywall-unlocking">
            <span className="unlocking-spinner" />
            <p>Transaction confirmed — loading content…</p>
            <p className="paywall-note">On-chain confirmation may take a moment.</p>
          </div>
        ) : (
          <>
            {error && <p className="paywall-error">{error}</p>}

            {viewType === 'ppv' && (
              <div className="paywall-option">
                <div className="option-label">
                  <span className="option-icon">🔑</span>
                  <div>
                    <strong>Pay-Per-View</strong>
                    <span className="option-desc">
                      Permanent access to this post
                      {quote?.pricing && (
                        <span className="option-fee-detail">
                          {' · '}
                          ~{formatPrice(quote.pricing.estimatedGas, { noSymbol: true })} {ppvTokenId || 'KLV'} network fee
                        </span>
                      )}
                    </span>
                  </div>
                  {ppvPrice && (
                    <span className="option-price">
                      {formatPrice(quote?.pricing?.totalTransaction ?? ppvPrice, { noSymbol: true })} {ppvTokenId || 'KLV'}
                    </span>
                  )}
                </div>
                {quote && !quote.canProceed && quote.warning && (
                  <div className="paywall-insufficient">
                    ⚠️ {quote.warning}
                  </div>
                )}
                <button
                  className="btn btn-primary paywall-btn"
                  onClick={handlePayPPV}
                  disabled={
                    isBusy || !ppvPrice || !profileId || quoteLoading || quote?.canProceed === false
                  }
                >
                  {step === 'signing'
                    ? (isEmailUser ? 'Processing…' : 'Waiting for wallet…')
                    : step === 'confirming'
                    ? 'Confirming…'
                    : quoteLoading
                    ? 'Calculating total…'
                    : quote?.canProceed === false && quote.balance.shortfall
                    ? `Need ${formatPrice(quote.balance.shortfall, { noSymbol: true })} more ${ppvTokenId || 'KLV'}`
                    : quote?.pricing?.totalTransaction
                    ? `Unlock for ${formatPrice(quote.pricing.totalTransaction, { noSymbol: true })} ${ppvTokenId || 'KLV'}`
                    : ppvPrice
                    ? `Unlock for ${formatPrice(ppvPrice, { noSymbol: true })} ${ppvTokenId || 'KLV'}`
                    : 'Price unavailable'}
                </button>
                {!profileId && (
                  <p className="paywall-error" style={{ marginTop: '0.5rem' }}>
                    Creator has no on-chain profile — contact support.
                  </p>
                )}
              </div>
            )}

            {viewType === 'paid' && (() => {
              // Each option only renders when its fee is non-zero so
              // the modal can't offer a path the creator hasn't priced.
              // If both are 0, fall back to a polite note instead of a
              // dead modal — the hero button is gated upstream, but
              // other entry points (post paywall, access panel) might
              // open this with stale fees.
              // Fees come from the API as display-unit strings — `'0'`,
              // `'0.00'`, `'0.00000000'` all mean "not configured". Use
              // parseFloat so any zero representation hides the option.
              const viewFeeEnabled = !!viewFee && parseFloat(viewFee) > 0;
              const subFeeEnabled = !!subscriptionFee && parseFloat(subscriptionFee) > 0;
              if (!viewFeeEnabled && !subFeeEnabled) {
                return (
                  <p className="paywall-note">
                    This creator hasn&apos;t set a subscription or view fee yet.
                  </p>
                );
              }
              return (
                <>
                  {viewFeeEnabled && (
                    <div className="paywall-option">
                      <div className="option-label">
                        <span className="option-icon">🔓</span>
                        <div>
                          <strong>One-Time View Fee</strong>
                          <span className="option-desc">Permanent access to all paid posts</span>
                        </div>
                        <span className="option-price">{formatPrice(viewFee)}</span>
                      </div>
                      <button
                        className="btn btn-primary paywall-btn"
                        onClick={handlePayViewFee}
                        disabled={isBusy || !profileId}
                      >
                        {isBusy ? 'Processing…' : `Pay ${formatPrice(viewFee)} (permanent)`}
                      </button>
                    </div>
                  )}
                  {subFeeEnabled && (
                    <div className="paywall-option paywall-option--secondary">
                      <div className="option-label">
                        <span className="option-icon">🔄</span>
                        <div>
                          <strong>Subscribe</strong>
                          <span className="option-desc">
                            Access while subscription is active
                            {subscriptionPeriodSeconds
                              ? ` · ${Math.round(subscriptionPeriodSeconds / 86400)} days`
                              : ''}
                          </span>
                        </div>
                        <span className="option-price">{formatPrice(subscriptionFee)}</span>
                      </div>
                      <button
                        className="btn btn-secondary paywall-btn"
                        onClick={handleSubscribe}
                        disabled={isBusy || !profileId}
                      >
                        {isBusy
                          ? (isEmailUser ? 'Processing…' : 'Waiting for wallet…')
                          : `Subscribe for ${formatPrice(subscriptionFee)}`}
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default PaywallModal;
